import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import CardDashboardItem from "components/CardDashboardItem";
import CardDashboardKPIChart from "components/CardDashboardKPIChart";
import {
  BarChart,
  ChartsTooltip,
  legendClasses,
  pieArcLabelClasses,
  PiePlot,
  ResponsiveChartContainer,
} from "@mui/x-charts";
import CardDashboardItemTripleData from "components/CardDashboardItemHorizontalMulti";
import ColumnFilter from "components/ColumnFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import {
  DataGrid,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import ClearIcon from "@mui/icons-material/ClearRounded";
import {
  normalizePhoneNumber,
  numericComparator,
} from "helpers/ServiceHelpers";
import api from "services/api";
import CEQuickFilter from "components/CEQuickFilter";
import { OVERLAY_NAMES } from "constants/Overlays";
import {
  Box,
  useTheme,
  ListItemIcon,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { EditRounded } from "@mui/icons-material";

const DashboardAllLinguists = ({ user, dateStart, dateEnd }) => {
  const { refreshPage, setOverlayData, setOverlayName, showSnackbar } =
    useStateContext();
  const theme = useTheme();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  const { handleErrorDialog } = useStateContext();

  // Set up entity tracking for Linguists list to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to LinguistsList so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "LinguistsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const [linguistsKPIs, setLinguistsKPIs] = useState();
  const [listOfLinguists, setlistOfLinguists] = useState();

  const [listofLanguages, setListOfLanguages] = useState("");
  const [listOfLinguiststatuses, setListOfLinguistStatuses] = useState("");
  const [listofLanguageModes, setListofLanguageModes] = useState("");

  const [filterBy, setFilterBy] = useState(null);

  const [title, setTitle] = useState("All Linguists");

  const [contextMenu, setContextMenu] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    if (!listOfLinguists) return;

    const secondData = {
      LinguistID: selectedRow,
      Linguist: listOfLinguists.find((linguist) => linguist.id === selectedRow),
      Languages: listofLanguages,
      LinguistStatuses: listOfLinguiststatuses,
      LanguageModes: listofLanguageModes,
      Action: OVERLAY_NAMES.LINGUIST_ACTION_EDIT,
    };

    setOverlayData(secondData);

    setOverlayName(OVERLAY_NAMES.LINGUIST_NEW_EDIT);

    setContextMenu(null);
  };

  const handleContextMenuCall = () => {
    if (!listOfLinguists) return;

    const selectedLinguist = listOfLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const phoneNumber = selectedLinguist.PhoneNumber;
      if (phoneNumber) {
        window.location.href = `tel:${phoneNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendEmail = () => {
    if (!listOfLinguists) return;

    const selectedLinguist = listOfLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const email = selectedLinguist.Email;
      if (email) {
        window.open(`mailto:${email}`, "_blank");
      } else {
        showSnackbar("No Email Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendSMS = () => {
    if (!listOfLinguists) return;

    const selectedLinguist = listOfLinguists.find(
      (linguist) => linguist.id === selectedRow,
    );
    if (selectedLinguist) {
      const phoneNumber = selectedLinguist.PhoneNumber;
      if (phoneNumber) {
        window.location.href = `sms:${phoneNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuOpenMaps = () => {
    if (!listOfLinguists) return;

    const linguist = listOfLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Address) {
        const address = encodeURIComponent(linguist.Address);
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${address}`,
          "_blank",
        );
      } else {
        showSnackbar("No Address Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyEmail = () => {
    if (!listOfLinguists) return;

    const linguist = listOfLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Email) {
        navigator.clipboard
          .writeText(linguist.Email)
          .then(() =>
            showSnackbar(
              "Email successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar("Failed to copy email to clipboard.", theme, "error"),
          );
      } else {
        showSnackbar("No Email Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyNumber = () => {
    if (!listOfLinguists) return;

    const linguist = listOfLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.PhoneNumber) {
        navigator.clipboard
          .writeText(linguist.PhoneNumber)
          .then(() =>
            showSnackbar(
              "Phone number successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy phone number to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar(
          "No Phone Number Available for this Linguist.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyAddress = () => {
    if (!listOfLinguists) return;

    const linguist = listOfLinguists.find(({ id }) => id === selectedRow);
    if (linguist) {
      if (linguist.Address) {
        navigator.clipboard
          .writeText(linguist.Address)
          .then(() =>
            showSnackbar(
              "Address successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy address to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar("No Address Available for this Linguist.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuAddTask = () => {
    if (!listOfLinguists) return;

    const linguistData = listOfLinguists.find(({ id }) => id === selectedRow);
    if (!linguistData) return;

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Linguist",
        EntityID: selectedRow,
      },
      Linguist: {
        id: linguistData.id,
        Name: linguistData.Name,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  // Helper functions to check if data is available
  const getSelectedLinguist = () => {
    if (!listOfLinguists) return null;
    return listOfLinguists.find((linguist) => linguist.id === selectedRow);
  };

  const hasPhoneNumber = () => {
    const linguist = getSelectedLinguist();
    return linguist?.PhoneNumber;
  };

  const hasEmail = () => {
    const linguist = getSelectedLinguist();
    return linguist?.Email;
  };

  const hasAddress = () => {
    const linguist = getSelectedLinguist();
    return linguist?.Address;
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Fetch static data for context menu (languages, statuses, modes) - only on mount
  useEffect(() => {
    api.get("/languages").then((response) => {
      if (response !== null) setListOfLanguages(response.data);
    });

    api.get("/linguists/linguistStatuses").then((response) => {
      if (response !== null) setListOfLinguistStatuses(response.data);
    });

    api.get("/linguists/languageModes").then((response) => {
      if (response !== null) setListofLanguageModes(response.data);
    });
  }, []);

  // Fetch all linguists data
  const fetchAllLinguistsData = useCallback(() => {
    api
      .all([
        api.get("/dashboard/all/linguists/linguistTotals"),
        api.get("/dashboard/all/linguists/linguists", {
          params: filterBy,
        }),
      ])
      .then(
        api.spread((linguistsKPIsResponse, linguistsResponse) => {
          if (linguistsKPIsResponse) {
            setLinguistsKPIs(linguistsKPIsResponse.data);
          }
          if (linguistsResponse) {
            setlistOfLinguists(linguistsResponse.data);
          }

          //Set loading to false after all data is loaded
          setIsLoading(false);
        }),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);

        setIsLoading(false); // Make sure to set loading to false on error
      });
  }, [filterBy, handleErrorDialog, theme]);

  useEffect(() => {
    fetchAllLinguistsData();
  }, [refreshPage, fetchAllLinguistsData]);

  // Listener callback for linguists list refetch
  const handleLinguistsListRefetch = useCallback(
    (updatedBy) => {
      setLastUpdate(updatedBy); // Store for snackbar
      fetchAllLinguistsData(); // Refetch data
    },
    [fetchAllLinguistsData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Linguist updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useAssignmentUpdates({
    modelType: "LinguistsList",
    recordId: "all",
    onUpdate: handleLinguistsListRefetch,
  });

  const linguistsByStatusPieData = [
    {
      id: 0,
      value: Number(linguistsKPIs?.byStatus?.Active || 0),
      label: "Active",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(linguistsKPIs?.byStatus?.Inactive || 0),
      label: "Inactive",
      color: theme.palette.error[400],
    },
    {
      id: 2,
      value: Number(linguistsKPIs?.byStatus?.["N/A"] || 0),
      label: "N/A",
      color: theme.palette.error[400],
    },
  ];

  const linguistsByCertificationPieData = [
    {
      id: 0,
      value: Number(linguistsKPIs?.byCertification?.Certified || 0),
      label: "Certified",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(linguistsKPIs?.byCertification?.["Non-certified"] || 0),
      label: "Non-certified",
      color: theme.palette.error[400],
    },
  ];

  const linguistsByContractPieData = [
    {
      id: 0,
      value: Number(linguistsKPIs?.byContract?.Yes || 0),
      label: "Have Contract",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(linguistsKPIs?.byContract?.No || 0),
      label: "Need Contract",
      color: theme.palette.error[400],
    },
  ];

  const linguistsByW9PieData = [
    {
      id: 0,
      value: Number(linguistsKPIs?.byW9?.Yes || 0),
      label: "Have W9",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(linguistsKPIs?.byW9?.No || 0),
      label: "Need W9",
      color: theme.palette.error[400],
    },
  ];

  const linguistsByBadgePieData = [
    {
      id: 0,
      value: Number(linguistsKPIs?.byBadge?.Yes || 0),
      label: "Have Badge",
      color: theme.palette.primary.main,
    },
    {
      id: 1,
      value: Number(linguistsKPIs?.byBadge?.No || 0),
      label: "Need Badge",
      color: theme.palette.error[400],
    },
    //{
    //  id: 2,
    //  value: Number(linguistsKPIs?.byBadge?.["N/A"] || 0),
    //  label: "N/A",
    //  color: theme.palette.error[400],
    //},
  ];

  const updateFilterBy = (data) => {
    const tempFilterBy = data.seriesId;
    let filterValue = "";
    let titleFilterBy = "Status";

    switch (tempFilterBy) {
      case "Status":
        titleFilterBy = "Status";
        switch (data.dataIndex) {
          case 0:
            filterValue = "Active";
            break;
          case 1:
            filterValue = "Inactive";
            break;
          case 2:
            filterValue = "N/A";
            break;
          default:
            break;
        }
        break;
      case "Certification":
        titleFilterBy = "Certification";
        switch (data.dataIndex) {
          case 0:
            filterValue = "Certified";
            break;
          case 1:
            filterValue = "Non-certified";
            break;
          default:
            break;
        }
        break;
      case "Badge":
        titleFilterBy = "Badge Status";
        switch (data.dataIndex) {
          case 0:
            filterValue = "Yes";
            break;
          case 1:
            filterValue = "No";
            break;
          case 2:
            filterValue = "N/A";
            break;
          default:
            break;
        }
        break;
      case "W9":
        titleFilterBy = "W9 Status";
        switch (data.dataIndex) {
          case 0:
            filterValue = "Yes";
            break;
          case 1:
            filterValue = "No";
            break;
          case 2:
            filterValue = "N/A";
            break;
          default:
            break;
        }
        break;
      case "Contract":
        titleFilterBy = "Contract Status";
        switch (data.dataIndex) {
          case 0:
            filterValue = "Yes";
            break;
          case 1:
            filterValue = "No";
            break;
          case 2:
            filterValue = "Filed";
            break;
          case 4:
            filterValue = "N/A";
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    setFilterBy({
      category: tempFilterBy,
      value: filterValue,
    });

    setTitle(
      "Showing All Linguists with a " +
        titleFilterBy +
        " of: " +
        filterValue +
        ":",
    );
  };

  const getPie = (data, id) => {
    const TOTAL = data.map((item) => item.value).reduce((a, b) => a + b, 0);

    const getArcLabel = (params) => {
      const percent = params.value / TOTAL;
      return `${(percent * 100).toFixed(0)}%`;
    };

    return (
      <ResponsiveChartContainer
        series={[
          {
            type: "pie",
            data: data,
            id: id,
            highlightScope: { faded: "global", highlighted: "item" },
            animated: true,
            arcLabel: getArcLabel,
            arcLabelMinAngle: 20,
            innerRadius: 50,
            outerRadius: 100,
            paddingAngle: 2,
            cornerRadius: 10,
          },
        ]}
        margin={{ top: 100, bottom: 100, left: 0, right: 0 }}
        sx={{
          "& path": {
            stroke: "transparent", // set the outline to yellow
            strokeWidth: 5, // increase the stroke width to show the border
          },
          [`& .${pieArcLabelClasses.root}`]: {
            fill: theme.palette.background.default,
            fontSize: 14,
            fontWeight: "bold",
          },
          [`& .${legendClasses.mark}`]: {
            ry: 6,
          },
        }}
      >
        {/** <ChartsLegend
          direction="row"
          position={{ vertical: "bottom", horizontal: "center" }}
          itemMarkWidth={20} // Adjust marker width
          itemMarkHeight={20} // Adjust marker height
          markGap={8} // Gap between marker and label
          itemGap={12} // Gap between legend items
        
        /> */}

        <ChartsTooltip
          trigger="item"
          labelFormatter={(itemData) => {
            return itemData.label || "";
          }}
          valueFormatter={(value, itemData) => {
            return `${value}${itemData.format === "percentage" ? "%" : ""}`;
          }}
        />

        <PiePlot onItemClick={(event, d) => updateFilterBy(d)} />
      </ResponsiveChartContainer>
    );
  };

  const translations = {
    total: "Total",
    Interpretation: "Interpretation",
    Translation: "Translation",
    OtherService: "Other Service",
  };

  function addLabels(series, format) {
    return series.map((item) => ({
      ...item,
      label: translations[item.dataKey],

      valueFormatter: (v) =>
        v
          ? format === "number"
            ? `${v.toLocaleString()}`
            : `$ ${v.toLocaleString()}`
          : "-",
    }));
  }

  // eslint-disable-next-line no-unused-vars
  const getStackedBar = (data, format) => {
    return (
      <BarChart
        dataset={data || []}
        series={addLabels(
          [
            {
              dataKey: "Translation",
              stack: "stack1",
              color: theme.palette.info.main, // Secondary service
            },
            {
              dataKey: "Interpretation",
              stack: "stack1",
              color: theme.palette.primary.main, // Primary service
            },
            {
              dataKey: "OtherService",
              stack: "stack1",
              color: theme.palette.grey[500], // Extra category
            },
          ],
          format,
        )}
        borderRadius={10}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "LinguistName",
            valueFormatter: (code, context) => {
              //const index = data.findIndex(item => item.shortName === code);
              //if(index !== -1) {
              //  return data[index].account;
              //}
              return code; // Fallback in case no match is found.
            },
            tickLabelStyle: {
              angle: -45,
              textAnchor: "end",
              //fontSize: 13,
            },
          },
        ]}
        yAxis={[
          {
            scaleType: "sqrt",
          },
        ]}
        slotProps={{
          legend: { hidden: true },
          tooltip: {
            contentRenderer: ({ payload }) => {
              return "testing";
            },
          },
        }}
        //width={600}
        height={450}
        margin={{ left: 100, top: 10, bottom: 110 }}
      />
    );
  };

  const columns = [
    {
      field: "Name",
      headerName: "Name",
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PhoneNumber",
      headerName: "Phone Number",
      valueGetter: (_, params) => {
        return normalizePhoneNumber(params.PhoneNumber);
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Email",
      headerName: "Email",
      minWidth: 200,
      flex: 2.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistLanguages.Rate",
      headerName: "Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.LinguistLanguages;
        if (!data || data.length === 0) {
          return "N/A";
        }
        const uniqueItems = new Set(
          data
            .filter((item) => item.Rate !== null)
            .map((item) => "$" + item.Rate),
        );

        const Items = Array.from(uniqueItems).join(" | ");

        return Items || "N/A";
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistLanguages.Languages",
      headerName: "Languages",
      valueGetter: (_, params) => {
        const data = params.LinguistLanguages;
        if (!data || data.length === 0) {
          return "N/A";
        }

        const uniqueItems = new Set(
          data
            .filter(
              (item) =>
                item.Language.Name !== null && item.Language.Name !== "",
            )
            .map((item) => item.Language.Name), // Extract rates
        );

        const items = Array.from(uniqueItems).join(" | ");

        return items || "N/A";
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },

    {
      field: "LinguistContractHistories.Status",
      headerName: "Contract Status",
      valueGetter: (_, params) => {
        const data = params.LinguistContractHistories;
        const value =
          data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[6] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },

    {
      field: "LinguistBadges.Status",
      headerName: "Badge Status",
      valueGetter: (_, params) => {
        const data = params.LinguistBadges;
        const value =
          data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[7] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistW9.Status",
      headerName: "W9 Status",
      valueGetter: (_, params) => {
        const data = params.LinguistW9s;
        const value =
          data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[8] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Certifications",
      headerName: "Certification",
      valueGetter: (_, params) => {
        const data = params.Certifications;
        if (!data || data.length === 0) {
          return "N/A";
        }

        // Assuming the list has only one item, get the first item
        const certification = data;
        if (!certification) {
          return "N/A";
        }

        // Check if the Name is "Other Certification" and return the appropriate value
        return certification;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[9] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Notes",
      headerName: "Notes",
      minWidth: 300,
      flex: 3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[10] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChange(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
          />
        </DataGridHeader>
      ),
    },
  ];

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const handleSortModelChange = (newSortModel) => {
    //setSortModel(newSortModel);
  };

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfLinguists || [], columns);

  // Wrapped clear all filters that also clears quick search
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
  }, [clearAllFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      { field: "Name", valueGetter: (row) => row.Name || "N/A" },
      {
        field: "PhoneNumber",
        valueGetter: (row) => normalizePhoneNumber(row.PhoneNumber),
      },
      { field: "Email", valueGetter: (row) => row.Email || "N/A" },
      { field: "Status", valueGetter: (row) => row.Status || "N/A" },
      {
        field: "LinguistLanguages.Rate",
        valueGetter: (row) => {
          const data = row.LinguistLanguages;
          if (!data || data.length === 0) return "N/A";
          const uniqueItems = new Set(
            data
              .filter((item) => item.Rate !== null)
              .map((item) => "$" + item.Rate),
          );
          return Array.from(uniqueItems).join(" | ") || "N/A";
        },
      },
      {
        field: "LinguistLanguages.Languages",
        valueGetter: (row) => {
          const data = row.LinguistLanguages;
          if (!data || data.length === 0) return "N/A";
          const uniqueItems = new Set(
            data
              .filter(
                (item) =>
                  item.Language.Name !== null && item.Language.Name !== "",
              )
              .map((item) => item.Language.Name),
          );
          return Array.from(uniqueItems).join(" | ") || "N/A";
        },
      },
      {
        field: "LinguistContractHistories.Status",
        valueGetter: (row) => {
          const data = row.LinguistContractHistories;
          return data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        },
      },
      {
        field: "LinguistBadges.Status",
        valueGetter: (row) => {
          const data = row.LinguistBadges;
          return data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        },
      },
      {
        field: "LinguistW9.Status",
        valueGetter: (row) => {
          const data = row.LinguistW9s;
          return data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        },
      },
      {
        field: "Certifications",
        valueGetter: (row) => row.Certifications || "N/A",
      },
      { field: "Notes", valueGetter: (row) => row.Notes || "N/A" },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column, index) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = listOfLinguists || [];
      } else {
        filteredData = (listOfLinguists || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(row);
            return selectedValues.includes(value);
          });
        });
      }

      // Limit data for columns with too many unique values
      if (filteredData.length > 0) {
        const valueCounts = {};
        filteredData.forEach((row) => {
          const value = column.valueGetter(row);
          valueCounts[value] = (valueCounts[value] || 0) + 1;
        });

        const topValues = Object.entries(valueCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 50)
          .map(([value]) => value);

        return filteredData.filter((row) => {
          const value = column.valueGetter(row);
          return topValues.includes(value);
        });
      }

      return filteredData;
    });
  }, [columnFilters, listOfLinguists, filterColumns]);

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, theme, title }) => (
      <GridToolbarContainer
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {title && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: theme.palette.primary.text2,
              margin: 0,
              padding: 0,
              alignSelf: "flex-start",
            }}
          >
            {title}
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          {hasAnyActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={onClear}
              variant="outlined"
              size="small"
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.buttonHoverSelected,
                  backgroundColor: theme.palette.primary.buttonHoverSelected,
                  color: theme.palette.background.default,
                },
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
          <CEQuickFilter />
        </Box>
      </GridToolbarContainer>
    );

    return Toolbar;
  }, []);

  const getDataGrid = (listOfServices, title) => {
    return (
      <>
        <DataGrid
          loading={isLoading}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100, { value: -1, label: "All" }]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          columns={columns}
          sortModel={sortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          disableColumnMenu
          disableColumnSorting
          onRowClick={(params, event, details) => {
            const data = {
              ComingFrom: "/dashboard",
              Linguist: params.row,
              LinguistID: params.row.id,
            };

            setOverlayData(data);

            navigate(`/linguists/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "✘" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: handleToolbarClear,
              theme,
              title,
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "0px",

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
              textAlign: "left !important",
              justifyContent: "flex-start !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "flex-start !important",
            },
            "& .MuiTypography-root.MuiTypography-body1": {
              textAlign: "left !important",
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
            "& .MuiDataGrid-scrollbar": {
              margin: "0 0 0 0",
            },
          }}
        />

        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
          <MenuItem onClick={handleContextMenuAddTask}>
            <ListItemIcon>
              <AddTaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Add Task</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCall}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <CallRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Call</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <EmailRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendSMS}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <SmsRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send SMS</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuOpenMaps}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <LocationOnRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Open Maps</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCopyEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyNumber}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Number</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyAddress}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Address</Typography>
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      size={{ xs: 12 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      <Grid size={{ xs: 12 }} sx={{ flexShrink: 0, px: 1, pt: 1 }}>
        <CardDashboardItemTripleData
          titles={["Total Linguists", "Total Languages"]}
          contents={[
            linguistsKPIs?.totalLinguists,
            linguistsKPIs?.totalLanguages,
          ]}
          isLoading={isLoading}
        />
      </Grid>

      <Box
        sx={{
          flex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          overflowY: { xs: "auto" },
          minHeight: 0,
          px: 1,
        }}
      >
        <Grid container spacing={2} sx={{ mb: 2, flex: "0 0 auto" }}>
          <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Linguists By Status"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(linguistsByStatusPieData, "Status")}
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Linguists By Certification"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(linguistsByCertificationPieData, "Certification")}
              isLoading={isLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 3 }}>
            <CardDashboardKPIChart
              title="Linguists By Contract"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(linguistsByContractPieData, "Contract")}
              isLoading={isLoading}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12 / 2, md: 12 / 2, lg: 12 / 2 }}>
            <CardDashboardKPIChart
              title="Linguists By Badge"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(linguistsByBadgePieData, "Badge")}
              isLoading={isLoading}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 / 2 }}>
            <CardDashboardKPIChart
              title="Linguists By W9"
              formatType="Number"
              thisMonth={null}
              lastMonth={null}
              percentage={null}
              chart={getPie(linguistsByW9PieData, "W9")}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            width: "100%",
            minHeight: { xs: 600 },
            flex: { xs: "0 0 auto" },
            pb: 1,
          }}
        >
          <CardDashboardItem
            title={null}
            content={getDataGrid(listOfLinguists, title)}
            isLoading={isLoading}
            height={{ xs: 600 }}
          />
        </Box>
      </Box>
    </Grid>
  );
};

export default DashboardAllLinguists;
